import { tagValues, type NostrEvent } from "@/nostr/types"
import { TOPICS, TOPIC_LEXICON, topicFromToken, type TopicId } from "./topicLexicon"

/**
 * Topic classification for the unified filter architecture.
 *
 * The classifier is a hybrid, in the order NIP data becomes available:
 *
 * 1. **explicit metadata** — `t` hashtags, `topic` / `category` / `genre` tags and
 *    NIP-32 `l` labels in a topic namespace;
 * 2. **content analysis** — a weighted, multilingual lexicon, where a term's
 *    weight says how much it alone proves the subject, so a passing mention is
 *    not a classification;
 * 3. **article metadata** — `title`, `summary`, `alt` and `description` tags of a
 *    post (NIP-23 articles) are weighed double.
 *
 * Nothing is published and no event is modified: results live in a local cache
 * keyed by the event id (a Nostr id is a hash of the event, so an entry can never
 * go stale).
 */
export type TopicMode = "include" | "exclude"

export const TOPIC_MODES: readonly TopicMode[] = ["include", "exclude"]

export interface TopicClassification {
  /** Best supported topic, when there is one. */
  primary?: TopicId
  /** Further topics with enough support. */
  secondary: TopicId[]
  /** `primary` followed by `secondary`. */
  all: TopicId[]
  /** Where the classification came from. */
  source: "tag" | "title" | "content" | "none"
  /** Confidence proxy: tag evidence plus the winning text score. */
  score: number
}

const UNCATEGORIZED: TopicClassification = { secondary: [], all: [], source: "none", score: 0 }

/** One weight class of a topic lexicon, compiled into regexes. */
interface CompiledGroup {
  patterns: RegExp[]
  weight: number
}

interface CompiledTopic {
  id: Exclude<TopicId, "other">
  groups: CompiledGroup[]
}

const BOUNDARY_START = "(?:^|[^\\p{L}\\p{N}])"
const BOUNDARY_END = "(?![\\p{L}\\p{N}])"
/** Scripts written without spaces: term boundaries would never match there. */
const CJK_TERM = /[\u3040-\u30ff\u3400-\u4dbf\u4e00-\u9fff\uac00-\ud7af]/

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
}

/** `politic*` -> `politic[\p{L}\p{N}]*`, `world cup` -> `world\s+cup`. */
function termPattern(term: string): string {
  const isPrefix = term.endsWith("*")
  const base = escapeRegExp(isPrefix ? term.slice(0, -1) : term).replace(/ +/g, "\\s+")
  return isPrefix ? `${base}[\\p{L}\\p{N}]*` : base
}

function compileGroup(terms: string[] | undefined, weight: number): CompiledGroup | undefined {
  if (!terms || terms.length === 0) return undefined
  const bounded: string[] = []
  const unbounded: string[] = []
  for (const term of new Set(terms)) {
    // Chinese / Japanese / Korean have no word separators, so those terms are
    // matched as substrings; everything else needs real word boundaries.
    if (CJK_TERM.test(term)) unbounded.push(termPattern(term))
    else bounded.push(termPattern(term))
  }
  const patterns: RegExp[] = []
  if (bounded.length > 0) {
    patterns.push(new RegExp(`${BOUNDARY_START}(${bounded.join("|")})${BOUNDARY_END}`, "giu"))
  }
  if (unbounded.length > 0) {
    patterns.push(new RegExp(`(${unbounded.join("|")})`, "giu"))
  }
  return { patterns, weight }
}

const COMPILED_TOPICS: CompiledTopic[] = (
  Object.entries(TOPIC_LEXICON) as Array<[Exclude<TopicId, "other">, (typeof TOPIC_LEXICON)["art"]]>
).map(([id, lexicon]) => ({
  id,
  groups: [
    compileGroup(lexicon.strong, 3),
    compileGroup(lexicon.medium, 2),
    compileGroup(lexicon.weak, 1),
  ].filter((group): group is CompiledGroup => Boolean(group)),
}))

const TOPIC_ORDER = new Map<TopicId, number>(TOPICS.map((id, index) => [id, index]))

interface TopicScore {
  score: number
  hits: Set<string>
}

/** Minimum evidence before a text may be called a topic. */
const MIN_SCORE = 4
/** Two different words, unless one word is repeated enough to be the subject. */
const MIN_DISTINCT = 2
const REPEATED_STRONG_SCORE = 6
/** An explicit tag always counts. */
const TAG_WEIGHT = 5
const MAX_SECONDARY = 3
/** A text runner-up must reach this share of the winner to be a secondary topic. */
const SECONDARY_SHARE = 0.4
const MAX_HITS_PER_GROUP = 3

function collect(text: string, multiplier: number, scores: Map<TopicId, TopicScore>): void {
  if (!text) return
  for (const topic of COMPILED_TOPICS) {
    let score = 0
    const hits = new Set<string>()
    for (const group of topic.groups) {
      let count = 0
      for (const pattern of group.patterns) {
        if (count >= MAX_HITS_PER_GROUP) break
        for (const match of text.matchAll(pattern)) {
          const hit = match[1].toLowerCase().replace(/\s+/g, " ").trim()
          if (hit) hits.add(hit)
          count += 1
          if (count >= MAX_HITS_PER_GROUP) break
        }
      }
      if (count > 0) score += count * group.weight * multiplier
    }
    if (score === 0) continue
    const entry = scores.get(topic.id) ?? { score: 0, hits: new Set<string>() }
    entry.score += score
    for (const hit of hits) entry.hits.add(hit)
    scores.set(topic.id, entry)
  }
}

const TITLE_TAGS = ["title", "summary", "alt", "description"] as const

/** Article / note metadata that states the subject explicitly. */
function titleText(event: NostrEvent): string {
  const parts: string[] = []
  for (const name of TITLE_TAGS) parts.push(...tagValues(event, name))
  return parts.join(" \n ")
}

/** Topic-ish tag names some clients use in addition to `t` hashtags. */
const TOPIC_TAG_NAMES = new Set(["t", "topic", "category", "genre"])
/** NIP-32 label namespaces that carry a topic. */
const TOPIC_LABEL_NAMESPACE = /topic|categor|subject|genre|tag/i

/** Explicitly declared topics, in tag order. */
export function topicTagsOf(event: NostrEvent): TopicId[] {
  const found: TopicId[] = []
  const push = (value: string | undefined) => {
    const topic = topicFromToken(value)
    if (topic && !found.includes(topic)) found.push(topic)
  }
  for (const tag of event.tags) {
    const [name, value, namespace] = tag
    if (!name || !value) continue
    if (TOPIC_TAG_NAMES.has(name)) {
      push(value)
      continue
    }
    if (name === "l") {
      // NIP-32 labels are namespaced; `l` is also used for languages.
      if (namespace && !TOPIC_LABEL_NAMESPACE.test(namespace)) continue
      push(value)
    }
  }
  return found
}

interface RankedTopic {
  id: TopicId
  score: number
  distinct: number
  fromTitle: boolean
}

/**
 * Pure classification (no cache). `classifyEvent` is the entry point the UI and
 * the filter engine use.
 */
export function classifyEventContent(event: NostrEvent): TopicClassification {
  const scores = new Map<TopicId, TopicScore>()
  const fromTitle = new Set<TopicId>()

  // Article / note metadata states the subject, so it counts double.
  const title = titleText(event)
  if (title) {
    collect(title, 2, scores)
    for (const id of scores.keys()) fromTitle.add(id)
  }
  collect(event.content ?? "", 1, scores)

  const ranked: RankedTopic[] = [...scores.entries()]
    .map(([id, entry]) => ({
      id,
      score: entry.score,
      distinct: entry.hits.size,
      fromTitle: fromTitle.has(id),
    }))
    .filter(
      (entry) =>
        entry.score >= MIN_SCORE &&
        (entry.distinct >= MIN_DISTINCT || entry.score >= REPEATED_STRONG_SCORE),
    )
    .sort((a, b) => b.score - a.score || (TOPIC_ORDER.get(a.id) ?? 0) - (TOPIC_ORDER.get(b.id) ?? 0))

  const tagged = topicTagsOf(event)
  if (tagged.length === 0 && ranked.length === 0) return UNCATEGORIZED

  const primary = tagged[0] ?? ranked[0]?.id
  if (!primary) return UNCATEGORIZED

  const topScore = ranked[0]?.score ?? 0
  const secondary: TopicId[] = []
  const candidates = [...tagged.slice(1), ...ranked.map((entry) => entry.id)]
  for (const id of candidates) {
    if (id === primary || secondary.includes(id)) continue
    if (!tagged.includes(id)) {
      const entry = ranked.find((item) => item.id === id)
      if (!entry) continue
      // A weak runner-up next to a strong winner is not a secondary subject.
      if (entry.score < Math.max(MIN_SCORE, topScore * SECONDARY_SHARE)) continue
    }
    secondary.push(id)
    if (secondary.length >= MAX_SECONDARY) break
  }

  const primaryRanked = ranked.find((entry) => entry.id === primary)
  const source: TopicClassification["source"] =
    tagged.length > 0 ? "tag" : primaryRanked?.fromTitle ? "title" : "content"

  return {
    primary,
    secondary,
    all: [primary, ...secondary],
    source,
    score: tagged.length * TAG_WEIGHT + (primaryRanked?.score ?? 0),
  }
}

const CACHE_LIMIT = 4000
const cache = new Map<string, TopicClassification>()
let classifications = 0

/**
 * Classified topics of an event, memoised by event id.
 *
 * A Nostr id is a hash of the event, so `content`, `tags` and `created_at` can
 * never change for a given id: a cached classification is reused across feeds,
 * pages and re-renders and never needs invalidating.
 */
export function classifyEvent(event: NostrEvent): TopicClassification {
  const cached = event?.id ? cache.get(event.id) : undefined
  if (cached) return cached
  const result = classifyEventContent(event)
  classifications += 1
  if (event?.id) {
    if (cache.size >= CACHE_LIMIT) {
      const oldest = cache.keys().next()
      if (!oldest.done) cache.delete(oldest.value)
    }
    cache.set(event.id, result)
  }
  return result
}

/** Topic ids of an event, primary first. */
export function topicsOf(event: NostrEvent): TopicId[] {
  return classifyEvent(event).all
}

/** True when the event matches the selection (OR across topics, or exclusion). */
export function matchesTopicFilter(
  event: NostrEvent,
  topics: readonly TopicId[] | undefined,
  mode: TopicMode = "include",
): boolean {
  if (!topics || topics.length === 0) return true
  const eventTopics = topicsOf(event)
  const hit = eventTopics.some((topic) => topics.includes(topic))
  // An exclusion only hides posts that actually carry the topic, so uncategorized
  // posts are never removed by a topic filter.
  return mode === "exclude" ? !hit : hit
}

/** Clears the memo (tests and a fresh session). */
export function clearTopicCache(): void {
  cache.clear()
  classifications = 0
}

/** Cache diagnostics: size and how many events were actually classified. */
export function topicCacheStats(): { size: number; classifications: number } {
  return { size: cache.size, classifications }
}

